import { Collapse, Radio } from '@blueprintjs/core';
import { ChevronDown, ChevronUp } from '@carbon/icons-react';
import * as React from 'react';
import { Checkbox, Icon } from 'vet-bones/design-system/components';

import { DataSourceTable } from 'src/constants/dataSourceTable';
import {
  getDataSourceTableCheckboxTestId,
  getDataSourceTableLabelTestId,
} from 'src/constants/testIds';
import * as copy from 'src/templates/copy';

import styles from 'src/containers/ProjectVirtualGraphPreviewDialogV2/components/virtualGraphTableTree.module.scss';

type VirtualGraphTableTreeProps = {
  tables: DataSourceTable[];
  // Single-select mode (radio buttons)
  selectedTable?: string | null;
  onSelectTable?: (tableName: string) => void;
  // Multi-select mode with parent checkboxes and separate preview
  checkedTables?: string[];
  onCheckTable?: (tableName: string) => void;
  onCheckAllInSchema?: (
    catalog: string,
    schema: string,
    tableNames: string[]
  ) => void;
  onCheckAllInCatalog?: (catalog: string, tableNames: string[]) => void;
  previewTable?: string | null;
  onPreviewTable?: (tableName: string) => void;
  // Common props
  searchQuery: string;
  multiSelect?: boolean;
  expandAll?: boolean;
  openCatalogs?: Set<string>;
  openSchemas?: Set<string>;
  onOpenCatalogsChange?: (catalogs: Set<string>) => void;
  onOpenSchemasChange?: (schemas: Set<string>) => void;
};

// Group tables by catalog/schema
type TableGroup = {
  [catalog: string]: {
    [schema: string]: DataSourceTable[];
  };
};

const groupTablesByCatalogAndSchema = (
  tables: DataSourceTable[]
): TableGroup => {
  const groups: TableGroup = {};

  tables.forEach((table) => {
    const catalog = table.catalog || 'Default';
    const schema = table.schema || 'Default';

    if (!groups[catalog]) {
      groups[catalog] = {};
    }
    if (!groups[catalog][schema]) {
      groups[catalog][schema] = [];
    }
    groups[catalog][schema].push(table);
  });

  return groups;
};

const filterTableGroups = (
  tableGroups: TableGroup,
  searchQuery: string
): TableGroup => {
  if (!searchQuery.trim()) {
    return tableGroups;
  }

  const filtered: TableGroup = {};
  const query = searchQuery.toLowerCase();

  Object.entries(tableGroups).forEach(([catalog, schemas]) => {
    Object.entries(schemas).forEach(([schema, tables]) => {
      const matchingTables = tables.filter((table) =>
        table.name.toLowerCase().includes(query)
      );

      if (matchingTables.length > 0) {
        if (!filtered[catalog]) {
          filtered[catalog] = {};
        }
        filtered[catalog][schema] = matchingTables;
      }
    });
  });

  return filtered;
};

const getGroupCheckboxState = (
  tableNames: string[],
  checkedTables: string[]
): { checked: boolean; indeterminate: boolean } => {
  if (tableNames.length === 0) {
    return { checked: false, indeterminate: false };
  }
  const checkedCount = tableNames.filter((name) =>
    checkedTables.includes(name)
  ).length;
  return {
    checked: checkedCount === tableNames.length,
    indeterminate: checkedCount > 0 && checkedCount < tableNames.length,
  };
};

/**
 * Extract all catalogs and schema paths (catalog/schema) from a TableGroup.
 * Returns sets that can be used to expand the tree.
 */
const getExpandedSetsFromTableGroups = (
  tableGroups: TableGroup
): { catalogs: Set<string>; schemas: Set<string> } => {
  const catalogs = new Set(Object.keys(tableGroups));
  const schemas = new Set<string>();

  Object.entries(tableGroups).forEach(([catalog, schemaObj]) => {
    Object.keys(schemaObj).forEach((schema) => {
      schemas.add(`${catalog}/${schema}`);
    });
  });

  return { catalogs, schemas };
};

export const VirtualGraphTableTree: React.VFC<VirtualGraphTableTreeProps> = ({
  tables,
  selectedTable,
  onSelectTable,
  checkedTables = [],
  onCheckTable,
  onCheckAllInSchema,
  onCheckAllInCatalog,
  previewTable,
  onPreviewTable,
  searchQuery,
  multiSelect = false,
  expandAll = false,
  openCatalogs: openCatalogsProp,
  openSchemas: openSchemasProp,
  onOpenCatalogsChange,
  onOpenSchemasChange,
}) => {
  const [openCatalogsInternal, setOpenCatalogsInternal] = React.useState<
    Set<string>
  >(new Set());
  const [openSchemasInternal, setOpenSchemasInternal] = React.useState<
    Set<string>
  >(new Set());

  // Controlled mode requires both openCatalogs and openSchemas to be provided
  const isControlled =
    openCatalogsProp !== undefined && openSchemasProp !== undefined;
  const openCatalogs = isControlled ? openCatalogsProp : openCatalogsInternal;
  const openSchemas = isControlled ? openSchemasProp : openSchemasInternal;

  const setOpenCatalogs = React.useCallback(
    (value: Set<string>) => {
      if (isControlled) {
        onOpenCatalogsChange?.(value);
      } else {
        setOpenCatalogsInternal(value);
      }
    },
    [isControlled, onOpenCatalogsChange]
  );

  const setOpenSchemas = React.useCallback(
    (value: Set<string>) => {
      if (isControlled) {
        onOpenSchemasChange?.(value);
      } else {
        setOpenSchemasInternal(value);
      }
    },
    [isControlled, onOpenSchemasChange]
  );

  const hasExpandedToSelection = React.useRef(false);
  const hasInitiallyExpanded = React.useRef(false);
  const prevTablesLength = React.useRef(tables.length);

  React.useEffect(() => {
    if (prevTablesLength.current !== tables.length) {
      hasExpandedToSelection.current = false;
      hasInitiallyExpanded.current = false;
      prevTablesLength.current = tables.length;
    }
  }, [tables.length]);

  React.useEffect(() => {
    if (hasExpandedToSelection.current) {
      return;
    }

    let selectedTableNames: string[] = [];
    if (multiSelect) {
      selectedTableNames = checkedTables || [];
    } else if (selectedTable) {
      selectedTableNames = [selectedTable];
    }

    if (tables.length === 0 || selectedTableNames.length === 0) {
      return;
    }

    const catalogs = new Set<string>();
    const schemas = new Set<string>();

    tables.forEach((table) => {
      if (selectedTableNames.includes(table.name)) {
        const catalog = table.catalog || 'Default';
        const schema = table.schema || 'Default';
        catalogs.add(catalog);
        schemas.add(`${catalog}/${schema}`);
      }
    });

    if (catalogs.size > 0) {
      setOpenCatalogs(catalogs);
      setOpenSchemas(schemas);
      hasExpandedToSelection.current = true;
    }
  }, [
    tables,
    selectedTable,
    checkedTables,
    multiSelect,
    setOpenCatalogs,
    setOpenSchemas,
  ]);

  const tableGroups = React.useMemo(
    () => groupTablesByCatalogAndSchema(tables),
    [tables]
  );

  const filteredTableGroups = React.useMemo(
    () => filterTableGroups(tableGroups, searchQuery),
    [tableGroups, searchQuery]
  );

  // Auto-expand filtered results
  React.useEffect(() => {
    if (searchQuery.trim()) {
      const { catalogs, schemas } =
        getExpandedSetsFromTableGroups(filteredTableGroups);
      setOpenCatalogs(catalogs);
      setOpenSchemas(schemas);
    }
  }, [searchQuery, filteredTableGroups, setOpenCatalogs, setOpenSchemas]);

  React.useEffect(() => {
    if (hasInitiallyExpanded.current || hasExpandedToSelection.current) {
      return;
    }

    if (Object.keys(tableGroups).length > 0) {
      const { catalogs, schemas } = getExpandedSetsFromTableGroups(tableGroups);
      setOpenCatalogs(catalogs);
      setOpenSchemas(schemas);
      hasInitiallyExpanded.current = true;
    }
  }, [tableGroups, setOpenCatalogs, setOpenSchemas]);

  React.useEffect(() => {
    if (expandAll) {
      const { catalogs, schemas } = getExpandedSetsFromTableGroups(tableGroups);
      setOpenCatalogs(catalogs);
      setOpenSchemas(schemas);
    }
  }, [expandAll, tableGroups, setOpenCatalogs, setOpenSchemas]);

  // Helper to get all table names in a catalog
  const getTablesInCatalog = React.useCallback(
    (catalog: string): string[] => {
      const schemas = filteredTableGroups[catalog];
      if (!schemas) {
        return [];
      }
      return Object.values(schemas).flatMap((schemaTables) =>
        schemaTables.map((t) => t.name)
      );
    },
    [filteredTableGroups]
  );

  // Helper to get all table names in a schema
  const getTablesInSchema = React.useCallback(
    (catalog: string, schema: string): string[] => {
      const schemaTables = filteredTableGroups[catalog]?.[schema];
      if (!schemaTables) {
        return [];
      }
      return schemaTables.map((t) => t.name);
    },
    [filteredTableGroups]
  );

  // Calculate catalog checkbox state
  const getCatalogCheckboxState = React.useCallback(
    (catalog: string) =>
      getGroupCheckboxState(getTablesInCatalog(catalog), checkedTables),
    [checkedTables, getTablesInCatalog]
  );

  // Calculate schema checkbox state
  const getSchemaCheckboxState = React.useCallback(
    (catalog: string, schema: string) =>
      getGroupCheckboxState(getTablesInSchema(catalog, schema), checkedTables),
    [checkedTables, getTablesInSchema]
  );

  // Handle catalog checkbox click - toggle all tables in catalog
  const handleCatalogCheckboxClick = React.useCallback(
    (catalog: string, e: React.MouseEvent) => {
      e.stopPropagation();
      const tableNames = getTablesInCatalog(catalog);
      onCheckAllInCatalog?.(catalog, tableNames);
    },
    [getTablesInCatalog, onCheckAllInCatalog]
  );

  // Handle schema checkbox click - toggle all tables in schema
  const handleSchemaCheckboxClick = React.useCallback(
    (catalog: string, schema: string, e: React.MouseEvent) => {
      e.stopPropagation();
      const tableNames = getTablesInSchema(catalog, schema);
      onCheckAllInSchema?.(catalog, schema, tableNames);
    },
    [getTablesInSchema, onCheckAllInSchema]
  );

  // Handle table checkbox click
  const handleTableCheckboxClick = React.useCallback(
    (tableName: string, e: React.MouseEvent) => {
      e.stopPropagation();
      onCheckTable?.(tableName);
      // Also select this table for preview if it's not already being previewed
      if (previewTable !== tableName) {
        onPreviewTable?.(tableName);
      }
    },
    [onCheckTable, previewTable, onPreviewTable]
  );

  // Handle table row click for preview (multi-select mode)
  const handleTableRowClick = React.useCallback(
    (tableName: string) => {
      if (multiSelect) {
        onPreviewTable?.(tableName);
      }
    },
    [multiSelect, onPreviewTable]
  );

  // Check if a table is checked (for new checkbox mode)
  const isTableChecked = React.useCallback(
    (tableName: string): boolean => {
      return checkedTables.includes(tableName);
    },
    [checkedTables]
  );

  // Check if a table is the preview table
  const isTablePreview = React.useCallback(
    (tableName: string): boolean => {
      return previewTable === tableName;
    },
    [previewTable]
  );

  const handleCatalogToggle = (catalog: string) => {
    const next = new Set(openCatalogs);
    if (next.has(catalog)) {
      next.delete(catalog);
    } else {
      next.add(catalog);
    }
    setOpenCatalogs(next);
  };

  const handleSchemaToggle = (catalogSchema: string) => {
    const next = new Set(openSchemas);
    if (next.has(catalogSchema)) {
      next.delete(catalogSchema);
    } else {
      next.add(catalogSchema);
    }
    setOpenSchemas(next);
  };

  // Handle single-select table selection (radio button)
  const handleTableSelect = (tableName: string) => {
    onSelectTable?.(tableName);
  };

  // Check if table is selected (single-select mode)
  const isTableSelected = (tableName: string): boolean => {
    return selectedTable === tableName;
  };

  const catalogEntries = Object.entries(filteredTableGroups);

  return (
    <div className={styles.virtualGraphTableTree}>
      {catalogEntries.map(([catalog, schemas]) => {
        const isCatalogOpen = openCatalogs.has(catalog);
        const catalogCheckboxState = multiSelect
          ? getCatalogCheckboxState(catalog)
          : null;

        return (
          <div key={catalog} className={styles.catalogNode}>
            <button
              className={styles.nodeHeader}
              onClick={() => handleCatalogToggle(catalog)}
              type="button"
            >
              {catalogCheckboxState ? (
                // Wrapper div to ensure clicks don't bubble to the toggle button
                <div onClick={(e) => e.stopPropagation()} role="presentation">
                  <Checkbox
                    checked={catalogCheckboxState.checked}
                    indeterminate={catalogCheckboxState.indeterminate}
                    onClick={(e) => handleCatalogCheckboxClick(catalog, e)}
                    size="md"
                  />
                </div>
              ) : null}
              <Icon
                IconComponent={isCatalogOpen ? ChevronUp : ChevronDown}
                size="sm"
              />
              <span className={styles.nodeLabel}>{catalog}</span>
            </button>
            <Collapse isOpen={isCatalogOpen}>
              <div className={styles.schemaContainer}>
                {Object.entries(schemas).map(([schema, schemaTables]) => {
                  const catalogSchemaKey = `${catalog}/${schema}`;
                  const isSchemaOpen = openSchemas.has(catalogSchemaKey);
                  const schemaCheckboxState = multiSelect
                    ? getSchemaCheckboxState(catalog, schema)
                    : null;

                  return (
                    <div key={catalogSchemaKey} className={styles.schemaNode}>
                      <button
                        className={styles.nodeHeader}
                        onClick={() => handleSchemaToggle(catalogSchemaKey)}
                        type="button"
                      >
                        {schemaCheckboxState ? (
                          // Wrapper div to ensure clicks don't bubble to the toggle button
                          <div
                            onClick={(e) => e.stopPropagation()}
                            role="presentation"
                          >
                            <Checkbox
                              checked={schemaCheckboxState.checked}
                              indeterminate={schemaCheckboxState.indeterminate}
                              onClick={(e) =>
                                handleSchemaCheckboxClick(catalog, schema, e)
                              }
                              size="md"
                            />
                          </div>
                        ) : null}
                        <Icon
                          IconComponent={isSchemaOpen ? ChevronUp : ChevronDown}
                          size="sm"
                        />
                        <span className={styles.nodeLabel}>{schema}</span>
                      </button>
                      <Collapse isOpen={isSchemaOpen}>
                        <div className={styles.tableContainer}>
                          {schemaTables.map((table) => {
                            const isSelected = isTableSelected(table.name);
                            const isChecked = isTableChecked(table.name);
                            const isPreview = isTablePreview(table.name);

                            // Determine the appropriate class names based on mode
                            let tableItemClassName = styles.tableItem;
                            if (multiSelect) {
                              // In checkbox mode, visual selection is based on preview, not checkbox state
                              if (isPreview) {
                                tableItemClassName += ` ${styles.tableItemPreview}`;
                              }
                              tableItemClassName += ` ${styles.tableItemClickable}`;
                            } else if (isSelected) {
                              tableItemClassName += ` ${styles.tableItemSelected}`;
                            }

                            // Multi-select mode with checkboxes
                            if (multiSelect) {
                              return (
                                <div
                                  key={table.name}
                                  className={tableItemClassName}
                                  onClick={() =>
                                    handleTableRowClick(table.name)
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      handleTableRowClick(table.name);
                                    }
                                  }}
                                  role="button"
                                  tabIndex={0}
                                >
                                  <div className={styles.tableItemRow}>
                                    <Checkbox
                                      checked={isChecked}
                                      onClick={(e) =>
                                        handleTableCheckboxClick(table.name, e)
                                      }
                                      size="md"
                                      testId={getDataSourceTableCheckboxTestId(
                                        table.name
                                      )}
                                    />
                                    <span
                                      className={styles.tableItemLabel}
                                      data-testid={getDataSourceTableLabelTestId(
                                        table.name
                                      )}
                                    >
                                      {table.name}
                                    </span>
                                  </div>
                                </div>
                              );
                            }

                            // Single-select mode with radio buttons
                            return (
                              <div
                                key={table.name}
                                className={tableItemClassName}
                              >
                                <Radio
                                  checked={isSelected}
                                  label={table.name}
                                  onChange={() => handleTableSelect(table.name)}
                                />
                              </div>
                            );
                          })}
                        </div>
                      </Collapse>
                    </div>
                  );
                })}
              </div>
            </Collapse>
          </div>
        );
      })}

      {catalogEntries.length === 0 ? (
        <div className={styles.noResults}>
          {copy.components.virtualGraphPreviewDialog.noTablesFound}
        </div>
      ) : null}
    </div>
  );
};
